import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Button, DialogContent, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { AlertsContext, DistaDialog, DistaDialogHeader } from "@sista/dista-ui";
import { useRequiredValidation, useSafeSistaAsync, } from "@sista/library-stasi";
import { useCallback, useContext, useMemo } from "react";
import { useForm } from "react-hook-form";
import { FormContainer, SelectElement, TextFieldElement, } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { useIsAdmin } from "../../util/useIsAdmin";
export const NewNamespaceDialog = ({ onSuccess, onClose, }) => {
    const { t } = useTranslation("sprava-textu");
    const { showSuccess, showInfo } = useContext(AlertsContext);
    const form = useForm({
        defaultValues: {
            namespace: "",
            format: "JSON",
        },
    });
    const spratex = useSpravaTextuClient();
    const isAdmin = useIsAdmin();
    const [createNamespaceCall, createNamespaceState] = useSafeSistaAsync(spratex.createNamespace);
    const [undeleteNamespaceCall] = useSafeSistaAsync(spratex.undeleteNamespace);
    const required = useRequiredValidation();
    /**
     * Prostor tohoto jména existuje, ale je smazaný (409 namespace.deleted.exists). Ve výpisu ho
     * přitom vidí jen admin, takže bez vysvětlení vypadá chyba jako by přišla z ničeho nic.
     */
    const isDeletedNamespaceConflict = useMemo(() => {
        const error = createNamespaceState.error;
        if (error == null)
            return false;
        return (error.status === 409 ||
            error.data?.code === 409 ||
            error.data?.code === "namespace.deleted.exists");
    }, [createNamespaceState.error]);
    const undeleteHandler = useCallback(async () => {
        const result = await undeleteNamespaceCall({
            namespace: form.getValues("namespace"),
        });
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.admin.newNamespace.namespaceUndeleted", "Jmenný prostor obnoven"));
            onSuccess?.();
            onClose?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        form.getValues,
        undeleteNamespaceCall,
        showSuccess,
        t,
        onSuccess,
        onClose,
    ]);
    const selectOptions = [
        {
            label: t("sprava-textu.admin.newNamespace.selectOpts.json", "json"),
            id: "JSON",
        },
        {
            label: t("sprava-textu.admin.newNamespace.selectOpts.properties", "properties"),
            id: "PROPERTIES",
        },
    ];
    const createAndCloseHandler = useCallback(async (data) => {
        const result = await createNamespaceCall(data);
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.admin.newNamespace.namespaceCreated", "Jmenný prostor vytvořen"));
            onSuccess();
        }
        onClose();
    }, [onClose, onSuccess, showSuccess, t, createNamespaceCall]);
    const createAndMoreHandler = useCallback(async (data) => {
        const result = await createNamespaceCall(data);
        if (typeof result === "object" && result) {
            showSuccess(t("sprava-textu.admin.newNamespace.namespaceCreated", "Jmenný prostor vytvořen"));
        }
        form.reset({
            namespace: "",
            format: "JSON",
        });
        // Reset the form to allow creating another namespace
        showInfo(t("sprava-textu.admin.newNamespace.namespaceFormCleared", "Formulář pro jmenný prostor vyčištěn"));
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClose, showSuccess, showInfo, form.reset, t, createNamespaceCall]);
    return (_jsxs(DistaDialog, { open: true, onClose: onClose, children: [_jsx(DistaDialogHeader, { title: t("sprava-textu.admin.newNamespace.newNamespace", "Nový jmenný prostor"), onClose: onClose }), _jsxs(DialogContent, { children: [isDeletedNamespaceConflict && (_jsx(Alert, { severity: "warning", sx: { mt: 2 }, action: isAdmin ? (_jsx(Button, { color: "inherit", size: "small", onClick: undeleteHandler, children: t("sprava-textu.admin.newNamespace.undeleteNamespace", "Obnovit") })) : null, children: isAdmin
                            ? t("sprava-textu.admin.newNamespace.deletedExistsAdmin", "Jmenný prostor tohoto názvu existuje, ale je smazaný. Můžete ho rovnou obnovit.")
                            : t("sprava-textu.admin.newNamespace.deletedExists", "Jmenný prostor tohoto názvu existuje, ale je smazaný. Obraťte se na administrátora.") })), _jsxs(FormContainer, { formContext: form, onSuccess: createAndCloseHandler, children: [_jsxs(Stack, { direction: "column", spacing: 2, sx: { my: 2 }, children: [_jsx(TextFieldElement, { name: "namespace", label: t("sprava-textu.admin.newNamespace.form.namespace", "Jmenný prostor"), validation: { ...required } }), _jsx(SelectElement, { name: "format", options: selectOptions, label: t("sprava-textu.admin.newNamespace.form.format", "Formát"), validation: { ...required } })] }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(Button, { variant: "contained", type: "submit", children: t("sprava-textu.admin.newNamespace.form.createAndClose", "Vytvořit a zavřít") })] })] })] })] }));
};
